import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useCrmText } from "@/lib/crm/i18n";
import { fieldLabel, type CrmEntity } from "@/lib/crm/fields";
import type { Draft } from "@/lib/crm/form-values";
import { isValidCep, onlyDigits } from "@/lib/masks";
import { CheckCircle2, ExternalLink, Loader2, MapPin, TriangleAlert } from "lucide-react";

/**
 * Address helpers: CEP lookup (ViaCEP), geocoding (OpenStreetMap Nominatim) and an embedded map.
 * Both services are free, keyless and CORS-enabled; calls happen only on user action.
 */

export interface CepResult { street: string; neighborhood: string; city: string; state: string }

/** null = CEP does not exist; throws on network failure. */
export async function lookupCep(cep: string): Promise<CepResult | null> {
  const digits = onlyDigits(cep);
  const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
  if (!res.ok) {
    if (res.status === 400) return null;
    throw new Error(`viacep ${res.status}`);
  }
  const data = await res.json();
  if (data?.erro) return null;
  return { street: data.logradouro || "", neighborhood: data.bairro || "", city: data.localidade || "", state: data.uf || "" };
}

export interface GeoResult { lat: number; lon: number; approximate: boolean }

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function nominatim(params: Record<string, string>): Promise<{ lat: number; lon: number } | null> {
  const qs = new URLSearchParams({ format: "jsonv2", limit: "1", "accept-language": "pt-BR", ...params });
  const res = await fetch(`https://nominatim.openstreetmap.org/search?${qs.toString()}`);
  if (!res.ok) throw new Error(`nominatim ${res.status}`);
  const list = await res.json();
  const hit = Array.isArray(list) ? list[0] : null;
  if (!hit) return null;
  const lat = Number(hit.lat), lon = Number(hit.lon);
  return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
}

/** Street + number first, then free text, then city level (approximate). Nominatim asks for ≤ 1 request/s. */
export async function geocodeAddress(a: { street: string; number: string; neighborhood: string; city: string; state: string; country: string; postalCode: string }): Promise<GeoResult | null> {
  const brazil = !a.country || /^bra[sz]il$/i.test(a.country.trim());
  const base: Record<string, string> = brazil ? { countrycodes: "br" } : {};
  const street = [a.number, a.street].filter(Boolean).join(" ");
  const attempts: Array<{ params: Record<string, string>; approximate: boolean }> = [];
  if (a.street && a.city) attempts.push({ params: { ...base, street, city: a.city, state: a.state, ...(brazil ? {} : { country: a.country }) }, approximate: false });
  const free = [a.street && `${a.street}${a.number ? `, ${a.number}` : ""}`, a.neighborhood, a.city && `${a.city}${a.state ? ` - ${a.state}` : ""}`, brazil ? "Brasil" : a.country]
    .filter(Boolean).join(", ");
  if (a.street) attempts.push({ params: { ...base, q: free }, approximate: false });
  if (a.city) attempts.push({ params: { ...base, city: a.city, state: a.state, ...(brazil ? {} : { country: a.country }) }, approximate: true });

  for (let i = 0; i < attempts.length; i++) {
    if (i > 0) await wait(1000);
    const hit = await nominatim(attempts[i].params);
    if (hit) return { ...hit, approximate: attempts[i].approximate };
  }
  return null;
}

/** Fields filled from a CEP, per entity (only empty fields, unless `overwrite`). */
export function cepPatch(entity: CrmEntity, draft: Draft, r: CepResult, overwrite: boolean): Partial<Draft> {
  const patch: Partial<Draft> = {};
  const put = (key: string, value: string) => {
    if (!value) return;
    if (overwrite || !String(draft[key] ?? "").trim()) patch[key] = value;
  };
  put("street1", r.street);
  put("city", r.city);
  put("stateProvince", r.state);
  if (entity === "lead") put("neighborhood", r.neighborhood);
  else put("street2", r.neighborhood);
  if (!String(draft.country ?? "").trim()) patch.country = "Brasil";
  return patch;
}

export const LEAD_ADDRESS_KEYS = ["postalCode", "street1", "addressNumber", "street2", "neighborhood", "city", "stateProvince", "country"];

/** Fills the address when a new complete CEP is typed and the field loses focus (once per CEP; never for the saved one). */
export function useCepAutofill(entity: CrmEntity, draft: Draft, setValues: (patch: Partial<Draft>) => void, savedCep?: string | null) {
  const last = useRef<string | null>(null);
  return async () => {
    const digits = onlyDigits(draft.postalCode);
    if (!isValidCep(digits) || digits === last.current || digits === onlyDigits(savedCep)) return;
    last.current = digits;
    try {
      const r = await lookupCep(digits);
      if (r) setValues(cepPatch(entity, draft, r, entity === "lead"));
    } catch { /* offline: the user types the address */ }
  };
}

type Message = { kind: "ok" | "warn" | "error"; text: string } | null;

/** Lead address: validate CEP + full address, geocode and show the map. */
export function LeadAddressPanel({ draft, setValues, readOnly }: { draft: Draft; setValues: (patch: Partial<Draft>) => void; readOnly: boolean }) {
  const { lang, tx } = useCrmText();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message>(null);

  const lat = typeof draft.latitude === "number" ? draft.latitude : null;
  const lon = typeof draft.longitude === "number" ? draft.longitude : null;

  const validate = async () => {
    setBusy(true);
    setMessage(null);
    try {
      let working: Draft = { ...draft };
      const cep = onlyDigits(working.postalCode);
      const brazil = !String(working.country || "").trim() || /^bra[sz]il$/i.test(String(working.country).trim());
      if (brazil) {
        if (!isValidCep(cep)) { setMessage({ kind: "error", text: tx("cepInvalid") }); return; }
        try {
          const r = await lookupCep(cep);
          if (!r) {
            setValues({ addressValidated: false });
            setMessage({ kind: "error", text: tx("cepNotFound") });
            return;
          }
          const patch = cepPatch("lead", working, r, true);
          working = { ...working, ...patch };
          setValues(patch);
        } catch {
          setMessage({ kind: "warn", text: tx("lookupFailed") });
        }
      }

      const required = ["postalCode", "street1", "addressNumber", "city", "stateProvince"];
      const missing = required.filter((k) => !String(working[k] ?? "").trim());
      if (missing.length) {
        setValues({ addressValidated: false });
        setMessage({ kind: "warn", text: tx("addressIncomplete", { fields: missing.map((k) => fieldLabel("lead", k, lang)).join(", ") }) });
        return;
      }

      const geo = await geocodeAddress({
        street: String(working.street1 || ""), number: String(working.addressNumber || ""), neighborhood: String(working.neighborhood || ""),
        city: String(working.city || ""), state: String(working.stateProvince || ""), country: String(working.country || ""), postalCode: cep,
      }).catch(() => null);
      if (!geo) {
        setValues({ addressValidated: false });
        setMessage({ kind: "warn", text: tx("addressNotFound") });
        return;
      }
      setValues({ latitude: Number(geo.lat.toFixed(6)), longitude: Number(geo.lon.toFixed(6)), addressValidated: !geo.approximate });
      setMessage(geo.approximate ? { kind: "warn", text: tx("approxLocation") } : { kind: "ok", text: tx("addressFound") });
    } finally {
      setBusy(false);
    }
  };

  const delta = 0.004;
  const embed = lat !== null && lon !== null
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lon - delta}%2C${lat - delta * 0.6}%2C${lon + delta}%2C${lat + delta * 0.6}&layer=mapnik&marker=${lat}%2C${lon}`
    : null;

  return (
    <div className="space-y-3 pt-1" data-testid="lead-address-panel">
      <div className="flex flex-wrap items-center gap-2">
        {!readOnly && (
          <Button type="button" variant="outline" size="sm" onClick={validate} disabled={busy} data-testid="button-validate-address">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            {busy ? tx("validating") : tx("validateAddress")}
          </Button>
        )}
        {draft.addressValidated ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400" data-testid="address-validated">
            <CheckCircle2 className="h-3.5 w-3.5" /> {tx("addressValidated")}
          </span>
        ) : lat !== null ? (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
            <TriangleAlert className="h-3.5 w-3.5" /> {tx("addressChanged")}
          </span>
        ) : null}
      </div>
      {message && (
        <p
          className={`text-xs ${message.kind === "ok" ? "text-emerald-700 dark:text-emerald-400" : message.kind === "warn" ? "text-amber-700 dark:text-amber-400" : "text-destructive"}`}
          role="status"
          data-testid="address-message"
        >
          {message.text}
        </p>
      )}
      {embed && (
        <div className="space-y-1.5">
          <iframe
            title={tx("mapTitle")}
            src={embed}
            className="h-56 w-full rounded-md border bg-muted"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="address-map"
          />
          <div className="flex flex-wrap gap-3 text-xs">
            <a className="inline-flex items-center gap-1 underline underline-offset-2" target="_blank" rel="noreferrer"
              href={`https://www.google.com/maps/search/?api=1&query=${lat}%2C${lon}`}>
              {tx("openGoogleMaps")} <ExternalLink className="h-3 w-3" />
            </a>
            <a className="inline-flex items-center gap-1 underline underline-offset-2" target="_blank" rel="noreferrer"
              href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`}>
              {tx("openOsm")} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
